// Lead Generation System — read-only manager feedback / coaching notes for the
// signed-in generator (newest-first). Opening this tab marks every unread note
// read server-side (the `mine()` call), so the sidebar "Feedback" badge clears
// once the load lands. Display only — the manager owns these.

import { useEffect } from "react";
import { MessageSquare } from "lucide-react";
import { Button, Card, EmptyState, Skeleton, Timestamp } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import { useWidgetData } from "../../dashboard/engine";
import { leadGenFeedbackApi } from "../../api/leadGenFeedback";
import { useLeadGenFeedbackBadge } from "../../components/PortalSidebar";

export function LeadGenFeedbackTab() {
  const { markRead } = useLeadGenFeedbackBadge();
  const feedback = useWidgetData(() => leadGenFeedbackApi.mine(), []);
  const items = feedback.data;

  // The `mine()` GET marks everything read server-side; mirror that on the
  // sidebar badge once the load succeeds.
  useEffect(() => {
    if (feedback.status === "ready") markRead();
  }, [feedback.status, markRead]);

  if (feedback.status === "loading" && items == null) {
    return (
      <div className="space-y-space-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (feedback.status === "error" && items == null) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          {feedback.error ?? "Couldn't load your feedback."}
        </p>
        <Button variant="text" size="sm" onClick={feedback.reload} className="mt-space-2">
          Try again
        </Button>
      </Card>
    );
  }
  if (!items || items.length === 0) {
    return (
      <EmptyState
        title="No feedback yet"
        description="When your manager leaves coaching notes, they'll show up here."
        icon={MessageSquare}
      />
    );
  }

  return (
    <ul className="flex flex-col gap-space-3 list-none p-0 m-0" data-testid="leadgen-feedback-list">
      {items.map((f) => (
        <li key={f.id}>
          <Card className="space-y-space-2">
            <div className="flex flex-wrap items-center justify-between gap-space-2">
              <span className="text-small font-display font-semibold text-onyx">{f.authorName}</span>
              <Timestamp value={f.createdAt} className="text-caption text-gray-mid" />
            </div>
            {f.weekOf && (
              <span className="text-caption text-gray-mid">
                Week of {formatCalendarDate(f.weekOf)}
              </span>
            )}
            <p className="text-small text-onyx m-0 whitespace-pre-wrap">{f.body}</p>
          </Card>
        </li>
      ))}
    </ul>
  );
}
